export const ZOOM_PRESETS = [25, 50, 75, 100, 150, 200] as const;
export const DEFAULT_MIN_ZOOM = 25;
export const DEFAULT_MAX_ZOOM = 200;
export const DEFAULT_ZOOM_STEP = 25;
export const DEFAULT_ZOOM = 100;

/**
 * Clamps a zoom value between a min and max.
 * @param zoom - The zoom value to clamp.
 * @param min - The minimum zoom value.
 * @param max - The maximum zoom value.
 * @returns The clamped zoom value.
 */
export function clampZoom(zoom: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, zoom));
}

/**
 * Calculates the zoom level needed to fit content within a viewport.
 * @param contentWidth - Natural width of the content.
 * @param contentHeight - Natural height of the content.
 * @param viewportWidth - Width of the viewport.
 * @param viewportHeight - Height of the viewport.
 * @param padding - Padding in pixels to subtract from viewport dimensions. Defaults to 32.
 * @returns The zoom percentage that fits the content without scrolling.
 */
export function calculateZoomToFit(
  contentWidth: number,
  contentHeight: number,
  viewportWidth: number,
  viewportHeight: number,
  padding: number = 32,
): number {
  if (contentWidth <= 0 || contentHeight <= 0 || viewportWidth <= 0 || viewportHeight <= 0) {
    return DEFAULT_ZOOM;
  }

  const availableWidth = viewportWidth - padding;
  const availableHeight = viewportHeight - padding;

  if (availableWidth <= 0 || availableHeight <= 0) {
    return DEFAULT_ZOOM;
  }

  const scaleX = availableWidth / contentWidth;
  const scaleY = availableHeight / contentHeight;
  const scale = Math.min(scaleX, scaleY);

  return Math.round(scale * 100);
}
